import type { ComponentType, FunctionComponent, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { css } from 'styled-components';

import { SorareLogo } from '#src/atoms/icons/SorareLogo/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BodyS, HeadlineM, HeadlineS } from '#src/atoms/typography/index.tsx';
import type {
  GraphQLResult,
  SubmitButtonProps,
} from '#src/components/form/Form/index.tsx';
import { GraphqlForm } from '#src/components/form/Form/index.tsx';
import { AcceptTermsAndPrivacyPolicyAndAgeLimit } from '#src/contexts/connection/PromptTermsDialog/AcceptTermsAndPrivacyPolicyAndAgeLimit/index.tsx';
import { AgreeToReceiveOffersFromPartners } from '#src/contexts/connection/PromptTermsDialog/AgreeToReceiveOffersFromPartners/index.tsx';
import type { AcceptTermsArgs } from '#src/contexts/connection/PromptTermsDialog/useAcceptTerms.tsx';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { messages as walletMessages } from '#src/contexts/wallet/messages.ts';
import { useToggle } from '#src/hooks/useToggle.ts';
import { glossary } from '#src/lib/glossary.ts';

const messages = defineMessages({
  required: {
    defaultMessage: 'Required',
  },
  optional: {
    defaultMessage: 'Optional',
  },
  justOneMoreStep: {
    defaultMessage: 'Just One More Step',
  },
});

const TitleSection = styled(HeadlineS)`
  text-align: center;
  padding: 0 24px;
  width: 100%;
`;

export const Title = () => {
  const { formatMessage } = useIntlContext();
  return <TitleSection as="h5">{formatMessage(glossary.signup)}</TitleSection>;
};

const Form = styled(GraphqlForm)`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px;
  margin-bottom: 0;
`;

const Section = styled.div`
  margin: 8px 0;
`;

const SorareHeader = styled(Horizontal).attrs({ gap: 0, center: true })`
  gap: 6px;
`;

const sectionInError = css`
  border: 2px solid var(--c-red-400);
`;

const AcceptTermsAndPrivacyPolicySection = styled.div<{ error?: boolean }>`
  ${props => (props.error ? sectionInError : '')}
`;

const StyledSorareLogo = styled(SorareLogo).attrs({
  color: 'var(--c-white)',
})`
  height: 16px;
`;
const JustOneMoreStepDiv = styled(HeadlineM)`
  text-align: center;
  margin: 24px;
`;

const ErrorMessage = styled.div`
  color: var(--c-red-400);
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  height: 56px;
  padding: var(--unit);
`;

type EffectiveAcceptTermsArgs = Omit<AcceptTermsArgs, 'tcuToken'>;

type Props = {
  onFormSuccess: (
    acceptedTerms: EffectiveAcceptTermsArgs,
    args: GraphQLResult
  ) => void;
  acceptTerms: (args: EffectiveAcceptTermsArgs) => Promise<GraphQLResult>;
};

const hasAcceptedMandatoryTerms = (terms: EffectiveAcceptTermsArgs) =>
  terms.acceptTerms && terms.acceptAgeLimit && terms.acceptPrivacyPolicy;

export const OneMoreStep = ({ onFormSuccess, acceptTerms }: Props) => {
  const { formatMessage } = useIntlContext();
  const [error, setError] = useState<ReactNode | null>(null);
  const [acceptTermsAndPrivacyPolicy, toggleAcceptTermsAndPrivacyPolicy] =
    useToggle(false);
  const [
    agreedToReceiveOffersFromPartners,
    toggleAgreedToReceiveOffersFromPartners,
  ] = useToggle(false);
  const [
    hasAttemptedSubmittingWithoutAccepting,
    setHasAttemptedSubmittingWithoutAccepting,
  ] = useState(false);

  const onError = useCallback(
    (result: GraphQLResult) => {
      if (result?.errors?.length) {
        setError(formatMessage(walletMessages.youMustAcceptTermsAndConditions));
      }
    },
    [setError, formatMessage]
  );

  const termsInput = useMemo<EffectiveAcceptTermsArgs>(
    () => ({
      acceptTerms: acceptTermsAndPrivacyPolicy,
      acceptAgeLimit: acceptTermsAndPrivacyPolicy,
      acceptPrivacyPolicy: acceptTermsAndPrivacyPolicy,
      agreedToReceiveOffersFromPartners,
      acceptGameRules: true, // technically, current terms imply game rules acceptance
    }),
    [acceptTermsAndPrivacyPolicy, agreedToReceiveOffersFromPartners]
  );

  const doAcceptTerms = useCallback(
    (values: any, onResult: (result: GraphQLResult) => void) => {
      setError(null);
      if (hasAcceptedMandatoryTerms(termsInput)) {
        acceptTerms(termsInput).then(result => onResult(result));
      } else {
        const youMustAcceptTermsAndConditions = formatMessage(
          walletMessages.youMustAcceptTermsAndConditions
        );
        setHasAttemptedSubmittingWithoutAccepting(true);
        onResult({
          error: youMustAcceptTermsAndConditions,
          errors: [
            {
              message: youMustAcceptTermsAndConditions,
            },
          ],
        });
      }
    },
    [acceptTerms, termsInput, formatMessage]
  );

  const youMustAcceptTermsAndConditions =
    hasAttemptedSubmittingWithoutAccepting &&
    !hasAcceptedMandatoryTerms(termsInput);

  const onSuccess = useCallback(
    (result: GraphQLResult) => onFormSuccess(termsInput, result),
    [onFormSuccess, termsInput]
  );

  return (
    <Form
      onSubmit={doAcceptTerms}
      onSuccess={onSuccess}
      onError={onError}
      render={(
        Error: ComponentType<React.PropsWithChildren<unknown>>,
        SubmitButton: FunctionComponent<
          React.PropsWithChildren<SubmitButtonProps>
        >
      ) => (
        <>
          <SorareHeader>
            <StyledSorareLogo color="currentColor" />
          </SorareHeader>
          <JustOneMoreStepDiv as="h4">
            <FormattedMessage {...messages.justOneMoreStep} />
          </JustOneMoreStepDiv>
          <BodyS bold>
            <FormattedMessage {...messages.required} />
          </BodyS>
          <Section>
            <AcceptTermsAndPrivacyPolicySection
              error={youMustAcceptTermsAndConditions}
            >
              <AcceptTermsAndPrivacyPolicyAndAgeLimit
                name="acceptTermsAndPrivacyPolicyLabel"
                checked={acceptTermsAndPrivacyPolicy}
                onChange={toggleAcceptTermsAndPrivacyPolicy}
              />
            </AcceptTermsAndPrivacyPolicySection>
            {!!youMustAcceptTermsAndConditions && !!error && (
              <ErrorMessage>{error}</ErrorMessage>
            )}
          </Section>
          <BodyS bold>
            <FormattedMessage {...messages.optional} />
          </BodyS>
          <Section>
            <AgreeToReceiveOffersFromPartners
              name="agreedToReceiveOffersFromPartners"
              checked={agreedToReceiveOffersFromPartners}
              onChange={toggleAgreedToReceiveOffersFromPartners}
            />
          </Section>
          <Actions>
            <SubmitButton size="medium" fullWidth>
              <FormattedMessage {...glossary.signup} />
            </SubmitButton>
          </Actions>
        </>
      )}
    />
  );
};
